import { useState } from 'react';
import { REPO } from '../config';
import type { FileStatus, Workspace, WorkspacePhase } from '../workcopy/workspace';
import { AlertIcon, DownloadIcon, FolderIcon } from './icons';

type Ready = Extract<WorkspacePhase, { kind: 'ready' }>;

/** "Reconnect your folder" (SPEC §4): access can lapse; requesting it again needs a click. */
export function ReconnectBanner({ folderName, onReconnect }: { folderName: string; onReconnect: () => Promise<void> }) {
  const [attempts, setAttempts] = useState(0);
  return (
    <div className="hq-banner" role="alert">
      <AlertIcon size={16} />
      Hashquill needs permission again to use your folder <strong>{folderName}</strong>.
      <button type="button" onClick={async () => { setAttempts((n) => n + 1); await onReconnect(); }}>Reconnect your folder</button>
      {attempts >= 3 && (
        <span>
          If Chrome stops asking: open Chrome settings → Privacy and security → Site settings → File editing, and allow this site.
        </span>
      )}
    </div>
  );
}

export function FolderGone({ onChooseAgain }: { onChooseAgain: () => void }) {
  return (
    <main className="hq-page hq-page--narrow">
      <span className="hq-icon-accent"><FolderIcon size={32} /></span>
      <h1 className="hq-title hq-title--small">Your specs folder was moved or deleted</h1>
      <p className="hq-lead">Hashquill can’t find it anymore. If you moved it, choose it in its new place. Unpushed work is kept inside that folder, so choose the same one if you can.</p>
      <div><button type="button" className="hq-btn" onClick={onChooseAgain}><FolderIcon />Choose folder</button></div>
    </main>
  );
}

export function OtherWindow({ onUseThisWindow }: { onUseThisWindow: () => void }) {
  return (
    <main className="hq-page hq-page--narrow">
      <span className="hq-icon-accent"><AlertIcon /></span>
      <h1 className="hq-title hq-title--small">Hashquill is open in another window</h1>
      <p className="hq-lead">Only one window can change your specs folder at a time, so nothing gets overwritten.</p>
      <div><button type="button" className="hq-btn" onClick={onUseThisWindow}>Use this window</button></div>
    </main>
  );
}

const STATUS_LABEL: Record<FileStatus['status'], string> = {
  pushed: 'Pushed',
  edited: 'Edited, not committed',
  new: 'New, not committed',
  deleted: 'Deleted, not committed',
};

export function FilesView({ phase, ws }: { phase: Ready; ws: Workspace }) {
  const changed = phase.files.filter((f) => f.status !== 'pushed');
  // Persisted in state.json, so it survives a restart (not just the last pull's result).
  const blocked = phase.state.needsResolving;
  return (
    <main className="hq-page hq-page--wide">
      <div className="hq-files-head">
        <div>
          <h1 className="hq-title hq-title--small">Specs</h1>
          <p className="hq-note">
            Folder <strong>{phase.folderName}</strong> · branch <code>{phase.state.branch}</code> · {phase.files.length} files
            {changed.length > 0 && <> · {changed.length} not committed</>}
          </p>
        </div>
        <div className="hq-action">
          <button type="button" className="hq-btn hq-btn--secondary" onClick={() => void ws.pull()} disabled={phase.pulling}>
            <DownloadIcon />{phase.pulling ? 'Pulling…' : 'Pull'}
          </button>
          <span className="hq-git">git pull origin {phase.state.branch}</span>
        </div>
      </div>

      {phase.lastPull && !phase.pulling && (
        <p className="hq-note" role="status">
          {phase.lastPull.updated.length || phase.lastPull.deleted.length
            ? `Pulled: ${phase.lastPull.updated.length} updated, ${phase.lastPull.deleted.length} removed.`
            : 'Already up to date.'}
        </p>
      )}
      {blocked.length > 0 && (
        <div className="hq-callout" role="alert">
          <AlertIcon size={16} />
          <div>
            <p><strong>Needs resolving · {blocked.length} {blocked.length === 1 ? 'file' : 'files'}</strong></p>
            <p>{blocked.join(', ')} changed both here and on GitHub. Pull kept your version and didn’t update {blocked.length === 1 ? 'it' : 'them'}.</p>
            <p className="hq-note">
              Push is blocked until this is resolved, so nothing on GitHub gets overwritten. Resolving conflicts arrives in a later update;
              until then, your changes stay safe in your folder.
            </p>
          </div>
        </div>
      )}

      <ul className="hq-filelist" aria-label={`Files in ${REPO.full}`}>
        {phase.files.map((f) => (
          <li key={f.path}>
            <span className={`hq-dot hq-dot--${f.status}`} aria-hidden="true" />
            <span className="hq-filelist__path">{f.path}</span>
            {f.stats && (f.stats.added > 0 || f.stats.removed > 0) && (
              <span className="hq-linecount" aria-label={`${f.stats.added} lines added, ${f.stats.removed} removed`}>
                <span className="hq-linecount__add">+{f.stats.added}</span> <span className="hq-linecount__del">−{f.stats.removed}</span>
              </span>
            )}
            {f.stats?.large && <span className="hq-flag" title="Much more of this file changed than usual. Check it before committing.">Unusually large change</span>}
            <span className="hq-filelist__status">{STATUS_LABEL[f.status]}</span>
          </li>
        ))}
      </ul>
      <p className="hq-note">Reviewing pull requests and editing come in the next updates.</p>
    </main>
  );
}
